import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ACCENT } from "../artistStudio/shared";
import { apiMessage, apiStatus } from "../fanHome/fanHomeApi";
import MusicShell from "../fanHome/MusicShell";
import { Notice } from "../fanHome/discoveryParts";
import { INK, MUTED, ListSkeleton, SolidButton } from "../fanHome/libraryParts";
import { money, formatDate } from "../soundDetail/soundApi";
import { receiptApi } from "./receiptApi";

// Receipt and invoice for one paid membership or licence, with a PDF download. There is no screen for it in the Claude
// design, so it follows the My Library look (white card, green accent). Everything shown comes from the document that was
// issued when the payment succeeded.
const TYPES = { membership: "membership", licence: "licence", renewal: "renewal", tip: "tip" };
const BACK = { membership: ["/fan-membership/details", "membership"], licence: ["/fan/library?tab=licences", "My Licences"], renewal: ["/fan/purchases", "Purchase History"], tip: ["/fan/purchases", "Purchase History"] };

function Block({ label, children }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: ACCENT }}>{label}</p>
      <div className="mt-1 text-[13px] leading-relaxed" style={{ color: MUTED }}>{children}</div>
    </div>
  );
}

export default function ReceiptPage() {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const sourceType = TYPES[type];
  const [state, setState] = useState({ status: "loading", doc: null, error: "", missing: false });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const load = useCallback(async () => {
    if (!sourceType || !/^\d+$/.test(String(id))) { setState({ status: "error", doc: null, error: "", missing: true }); return; }
    setState((s) => ({ ...s, status: "loading", error: "" }));
    try {
      setState({ status: "ready", doc: await receiptApi.bySource(sourceType, id), error: "", missing: false });
    } catch (e) {
      setState({ status: "error", doc: null, error: apiMessage(e, "We couldn't load this receipt."), missing: apiStatus(e) === 404 });
    }
  }, [sourceType, id]);
  useEffect(() => { load(); }, [load]);

  const [backTo, backLabel] = BACK[sourceType] || BACK.licence;
  const cameFromApp = location.key !== "default";
  const goBack = () => (cameFromApp ? navigate(-1) : navigate(backTo));

  const download = async () => {
    if (saving) return;
    setSaving(true);
    setSaveError("");
    try {
      await receiptApi.downloadPdf(state.doc);
    } catch (e) {
      setSaveError("We couldn't download the PDF. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const doc = state.doc;
  const refunded = doc?.status === "refunded";

  return (
    <MusicShell>
      <button type="button" onClick={goBack} className="text-[13px] font-semibold hover:underline" style={{ color: MUTED }}>← Back to {backLabel}</button>
      {state.status === "loading" ? (
        <div className="mt-6"><ListSkeleton rows={3} /></div>
      ) : state.status === "error" ? (
        <div className="mt-6">
          {state.missing
            ? <Notice action={<Link to={backTo} className="mt-3 inline-block rounded-full px-[18px] py-2 text-[13px] font-semibold text-white" style={{ backgroundColor: ACCENT }}>Go back</Link>}>There is no receipt for this purchase. It may belong to another account, or the payment was not completed.</Notice>
            : <Notice action={<SolidButton onClick={load} className="mt-3">Retry</SolidButton>}><span role="alert">{state.error}</span></Notice>}
        </div>
      ) : (
        <article className="mt-4 max-w-[720px] rounded-2xl border bg-white p-6 md:p-8" style={{ borderColor: "#e0e8e3" }} data-testid="receipt">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-[22px] font-bold" style={{ color: INK }}>{doc.title}</h1>
              <p className="mt-1 text-[13px]" style={{ color: MUTED }}>Number {doc.document_number}</p>
              <p className="text-[13px]" style={{ color: MUTED }}>Date {formatDate(doc.issued_at)}</p>
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${refunded ? "bg-amber-50 text-amber-800" : "bg-green-50"}`} style={refunded ? undefined : { color: ACCENT }}>{doc.status_label}</span>
          </div>

          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <Block label="From">
              <p className="font-semibold" style={{ color: INK }}>{doc.seller?.name}</p>
              {(doc.seller?.address_lines || []).map((l) => <p key={l}>{l}</p>)}
              {doc.seller?.tax_id && <p>Tax ID: {doc.seller.tax_id}</p>}
              {doc.seller?.email && <p>{doc.seller.email}</p>}
            </Block>
            <Block label="Billed to">
              {doc.buyer?.name && <p className="font-semibold" style={{ color: INK }}>{doc.buyer.name}</p>}
              {doc.buyer?.email && <p>{doc.buyer.email}</p>}
              {doc.buyer?.billing_address && <p>{doc.buyer.billing_address}</p>}
            </Block>
          </div>

          <div className="mt-6 border-t pt-4" style={{ borderColor: "#e0e8e3" }}>
            {doc.lines.map((line, i) => (
              <div key={i} className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-[15px] font-semibold" style={{ color: INK }}>{line.description}</p>
                  {line.detail && <p className="text-[13px]" style={{ color: MUTED }}>{line.detail}</p>}
                </div>
                <p className="shrink-0 text-[15px]" style={{ color: INK }}>{money(line.amount, doc.currency)}</p>
              </div>
            ))}
          </div>

          <dl className="mt-5 ml-auto max-w-[320px] space-y-1.5 border-t pt-4 text-[14px]" style={{ borderColor: "#e0e8e3" }}>
            <div className="flex justify-between gap-4"><dt style={{ color: MUTED }}>Subtotal</dt><dd style={{ color: INK }}>{money(doc.subtotal, doc.currency)}</dd></div>
            <div className="flex justify-between gap-4"><dt style={{ color: MUTED }}>{doc.tax.label}</dt><dd style={{ color: INK }}>{money(doc.tax.amount, doc.currency)}</dd></div>
            {doc.platform_fee && <div className="flex justify-between gap-4"><dt style={{ color: MUTED }}>{doc.platform_fee.label}</dt><dd style={{ color: INK }}>{money(doc.platform_fee.amount, doc.currency)}</dd></div>}
            <div className="flex justify-between gap-4 text-[16px] font-bold" style={{ color: INK }}><dt>Total</dt><dd>{money(doc.total, doc.currency)}</dd></div>
          </dl>

          <p className="mt-5 text-[13px]" style={{ color: MUTED }}>
            Payment: {doc.payment.method}{doc.payment.reference ? `, reference ${doc.payment.reference}` : ""}
            {refunded && " This purchase has been refunded."}
          </p>
          {doc.payment.simulated && <p className="mt-1 text-[13px] text-amber-700">Test payment: no money was charged.</p>}

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <SolidButton onClick={download} disabled={saving} data-testid="download-receipt">{saving ? "Preparing PDF…" : "Download PDF"}</SolidButton>
            {saveError && <span role="alert" className="text-[13px] text-red-700">{saveError}</span>}
          </div>
        </article>
      )}
    </MusicShell>
  );
}
